import './index.less';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DetectionsTabs from './DetectionsTabs';
import {
  Flex,
  Text,
  Box,
  Stack,
  SearchInput,
  Menu,
  MenuButton,
  TextLabel,
  MenuList,
  MenuItem,
  Icon,
} from '@tonic-one/react';
import { ColumnsIcon, ExportIcon, RefreshIcon } from '@tonic-one/react-icons';
import { useOrgStore } from '@Models/useOrgModel';
import { debounce } from '@Utils/common';
import Dropdown from '@Components/Common/Dropdown';
import { MenuOption } from '@Types/index';
import SmartTooltip from '@Report/SmartTooltip';
import { QuarantineProvider, useQuarantineStore } from '@Quarantine/store';
import { RiskAiRiskType } from './types';
import { useLocation, useSearchParams } from 'react-router-dom';
import ManageColumnsModal from './ManageColumnsModal';
import { getColumnsForExport, mapRiskAiDetectedBy } from './utils';
import TimeRange from '@Log/TimeRange';
import { getTimeRange, LogTimeUnit } from '@Log/shared';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import ExportModal from './ExportModal';
import { dayjs } from '@Utils/time';
import {
  LogTypeEnum,
  QuarantineLogTypeEnum,
  QuarantineRiskAILogTypeEnum,
} from '@Types/log';
import IconButton from '@Components/IconButton';
import useRiskTypeBasedSelector from '@Quarantine/store/useRiskTypeBasedSelector';
import { queryClient } from 'src/QueryClient';
import { useDetectionTableQueryKey } from './DetectionsTable/useDetectionTableQueryKey';

const RiskAi = () => {
  const [filterInitialized, setFilterInitialized] = useState(false);

  return (
    <Flex flex="1 1 auto" flexDirection="column">
      <HeaderPart onInitializeFilters={() => setFilterInitialized(true)} />
      <DetectionsTabs filterInitialized={filterInitialized} />
    </Flex>
  );
};

function RiskAiWithStore() {
  const { orgId, orgsMap } = useOrgStore();
  const { offset } = useUtcOffsetStore();
  const [searchParams] = useSearchParams();

  const storeOrgId = useMemo(() => {
    const companyGuid = searchParams.get('orgId');
    if (companyGuid && orgsMap.has(companyGuid)) return companyGuid;
    return orgId;
  }, [orgId, orgsMap, searchParams]);

  return (
    <QuarantineProvider orgId={storeOrgId} utcOffset={offset}>
      <RiskAi />
    </QuarantineProvider>
  );
}

export default RiskAiWithStore;

const HeaderPart = (props: { onInitializeFilters: () => void }) => {
  const { onInitializeFilters } = props;
  const { t } = useTranslation();
  const { orgId, orgsMap, orgs, switchOrgId } = useOrgStore();
  const {
    tagFilter,
    timeFilter,
    riskTypeFilter,
    serviceFilter,
    detectedByFilter,
  } = useQuarantineStore((state) => state.riskAi.filters);
  const { riskNameFilter, riskNameFilterName, columnConfig, setColumnConfig } =
    useRiskTypeBasedSelector();
  const { setFilter, setCustomTimeRange, sortOrder } = useQuarantineStore(
    (state) => state.riskAi
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { offset } = useUtcOffsetStore();
  const [isColumnsModalOpen, setIsColumnsModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const debounceSetSearch = debounce((value: string) => {
    setFilter('searchFilter', value);
  }, 200);

  const queryKey = useDetectionTableQueryKey({ sortOrder });

  const handleReloadTableData = useCallback(async () => {
    await queryClient.resetQueries({
      queryKey,
    });
  }, [queryKey]);

  const { tagFilterIds, tagFilterIdNameMap } = useMemo(() => {
    const tagFilterIds: string[] = [];
    const tagFilterIdNameMap: Record<string, string> = {};
    tagFilter.values.forEach(({ id, name }) => {
      if (!id || !name) return;
      tagFilterIds.push(id);
      tagFilterIdNameMap[id] = name;
    });
    return { tagFilterIds, tagFilterIdNameMap };
  }, [tagFilter]);

  useEffect(() => {
    const riskName = searchParams.get(riskNameFilterName);
    if (riskName) {
      setFilter(riskNameFilterName, riskName);
    }
    const companyGuid = searchParams.get('orgId');
    if (companyGuid && orgsMap.has(companyGuid)) {
      switchOrgId(companyGuid);
    } else if (companyGuid) {
      searchParams.delete('orgId');
      setSearchParams(searchParams);
    }
    const timeFilter = searchParams.get('timeFilter') as LogTimeUnit;
    const isCustom = timeFilter === LogTimeUnit.Custom;
    if (
      timeFilter &&
      !isCustom &&
      Object.values(LogTimeUnit).includes(timeFilter)
    ) {
      setFilter('timeFilter', timeFilter);
    } else if (isCustom) {
      const timeStart = searchParams.get('timeStart');
      const timeEnd = searchParams.get('timeEnd');
      if (timeStart && timeEnd) {
        setCustomTimeRange({
          predefined: LogTimeUnit.Custom,
          start: timeStart,
          end: timeEnd,
          timezoneOffset: offset,
        });
      }
    }
    onInitializeFilters();
  }, [
    location.pathname,
    searchParams,
    offset,
    orgsMap,
    riskNameFilterName,
    setFilter,
    switchOrgId,
    setSearchParams,
    setCustomTimeRange,
    onInitializeFilters,
  ]);

  const columnNames = useMemo(
    () =>
      Object.keys(columnConfig.i18nKeys).reduce(
        (acc, key) => {
          acc[key] = t(columnConfig.i18nKeys[key]);
          return acc;
        },
        {} as Record<string, string>
      ),
    [t, columnConfig]
  );

  return (
    <Box px="4x">
      <Text pt="4x">{t('riskAi.tab.description')}</Text>
      <Text size="xl" py="4x">
        {t('riskAi.detail.title')}
      </Text>
      <Flex mb="3x">
        <Stack
          width="max-content"
          direction="row"
          flex="1 1 auto"
          flexWrap="wrap"
          gap="2x"
        >
          <OrgMenu
            menuItems={orgs || []}
            selectedValue={orgsMap.get(orgId) as string}
            onClick={(e: any) => {
              if (e.target.getAttribute('value') === orgId) return;
              const newOrgId = e.target.getAttribute('value');
              switchOrgId(newOrgId);
              const companyGuid = searchParams.get('orgId');
              if (companyGuid && companyGuid !== newOrgId) {
                searchParams.delete('orgId');
                setSearchParams(searchParams);
              }
            }}
          />
          <Dropdown
            placeholder={t('riskAi.filter.serviceName')}
            minWidth={150}
            maxWidth={250}
            menuHeight={400}
            menuWidth={250}
            value={serviceFilter.currentValue}
            options={serviceFilter.values}
            renderOption={(option) =>
              option === QuarantineRiskAILogTypeEnum.All
                ? t('common.label.all')
                : t(`common.service.${option}`)
            }
            onChange={(value) => setFilter('serviceFilter', value)}
          />
          {riskTypeFilter.currentValue === RiskAiRiskType.SecurityRisk && (
            <Dropdown
              placeholder={t('riskAi.table.threat.header.detectedBy')}
              minWidth={225}
              maxWidth={275}
              menuHeight={400}
              value={detectedByFilter.currentValue}
              options={detectedByFilter.values}
              renderOption={(option) => {
                if (option === 'all') return t('common.label.all');
                return mapRiskAiDetectedBy(option, t);
              }}
              onChange={(value) => setFilter('detectedByFilter', value)}
            />
          )}
          <Dropdown
            placeholder={t('riskAi.filter.analysisTag.placeholder')}
            minWidth={225}
            maxWidth={275}
            menuHeight={400}
            value={tagFilter.currentValue.id}
            options={tagFilterIds}
            renderOption={(id) =>
              id === 'all' ? t('common.label.all') : tagFilterIdNameMap[id]
            }
            onChange={(id) =>
              setFilter('tagFilter', { id, name: tagFilterIdNameMap[id] })
            }
          />
          <Dropdown
            placeholder={
              [
                RiskAiRiskType.Anomaly,
                RiskAiRiskType.CustomizedAnomaly,
              ].includes(riskTypeFilter.currentValue)
                ? t('riskAi.table.threat.header.anomalyName')
                : t('riskAi.table.threat.header.securityRiskName')
            }
            minWidth={225}
            maxWidth={275}
            menuHeight={400}
            value={riskNameFilter.currentValue}
            options={riskNameFilter.values}
            renderOption={(option) =>
              option === 'all' ? t('common.label.all') : option
            }
            onChange={(value) => {
              setFilter(riskNameFilterName, value);
              const riskName = searchParams.get(riskNameFilterName);
              if (riskName && riskName !== value) {
                searchParams.delete(riskNameFilterName);
                setSearchParams(searchParams);
              }
            }}
          />
          <TimeRange
            type={LogTypeEnum.DETECTION}
            timeRange={
              timeFilter.custom ??
              getTimeRange(
                timeFilter.currentValue,
                QuarantineLogTypeEnum.Exchange
              )
            }
            showLabel={false}
            width={225}
            placeholder={t('riskAi.filter.timeRange')}
            setTimeRange={(timeRange) => {
              if (timeRange.predefined === LogTimeUnit.Custom) {
                setCustomTimeRange(timeRange);
                return;
              }
              setFilter('timeFilter', timeRange.predefined);
              searchParams.delete('timeFilter');
              searchParams.delete('timeStart');
              searchParams.delete('timeEnd');
              setSearchParams(searchParams);
            }}
          />
          <SearchInput
            width="325px"
            placeholder={t('riskAi.filter.search')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              debounceSetSearch(e.target.value);
            }}
            onClearInput={() => setFilter('searchFilter', '')}
          />
        </Stack>
        <Flex flexWrap="nowrap" height="max-content" mt="auto">
          <IconButton
            tooltipLabel={t('riskAi.table.refresh')}
            width={32}
            height={32}
            onClick={handleReloadTableData}
          >
            <Icon as={RefreshIcon} />
          </IconButton>
          <IconButton
            tooltipLabel={t('log.button.manageColumns')}
            width={32}
            height={32}
            onClick={() => setIsColumnsModalOpen(true)}
          >
            <Icon as={ColumnsIcon} />
          </IconButton>
          <IconButton
            tooltipLabel={t('log.button.export')}
            width={32}
            height={32}
            onClick={() => setIsExportModalOpen(true)}
          >
            <Icon as={ExportIcon} />
          </IconButton>
        </Flex>
      </Flex>
      {isColumnsModalOpen && (
        <ManageColumnsModal
          visible={isColumnsModalOpen}
          columnOrder={columnConfig.order}
          columnNames={columnNames}
          columnHideIds={columnConfig.hideIds}
          onSave={(order, hideIds) => {
            setColumnConfig({ ...columnConfig, order, hideIds });
          }}
          onClose={() => setIsColumnsModalOpen(false)}
        />
      )}
      {isExportModalOpen && (
        <ExportModal
          visible={isExportModalOpen}
          columns={getColumnsForExport(columnConfig, t)}
          renderData={(contentList) => {
            return contentList
              .sort((a, b) => dayjs(b.timestamp).diff(dayjs(a.timestamp)))
              .map((content) => {
                return {
                  ...content,
                  anomalyName: content.securityRiskName,
                  timestamp: dayjs(content.timestamp)
                    .utcOffset(offset)
                    .format(t('common.momentDateFormat')),
                  matchedFilters: content.matchedFilters?.join(', ') as any,
                  action: t(`quarantine.action.${content.action}`),
                  companyGuid:
                    orgsMap.get(content.companyGuid) || content.companyGuid,
                };
              });
          }}
          onClose={() => setIsExportModalOpen(false)}
        />
      )}
    </Box>
  );
};

interface MenuProps {
  menuItems: MenuOption[];
  selectedValue: string;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const OrgMenu = ({ menuItems, selectedValue, onClick }: MenuProps) => {
  const { t } = useTranslation();
  return (
    <Menu>
      <MenuButton variant="secondary" width="288px">
        <Flex maxWidth="240px">
          <TextLabel mr="2x">{t('common.label.organization')}:</TextLabel>
          <SmartTooltip label={selectedValue}>{selectedValue}</SmartTooltip>
        </Flex>
      </MenuButton>
      <MenuList width="288px" maxHeight="400px" overflow="auto">
        {menuItems.map((item) => (
          <MenuItem onClick={onClick} key={item.key} value={item.key}>
            {item.value}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};
